import { Link } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { avatarStyle, initialsOf } from '@/features/crm/lib/avatar';
import { MultiSelect } from '@/features/crm/components/MultiSelect';
import { ListActions } from '@/features/crm/components/ListActions';
import {
  BAND_COLOR,
  SCORED_BANDS,
  type HealthBand,
} from '@/features/crm/lib/health-bands';

/**
 * The bands, as the server now defines them (api src/lib/health.ts).
 *
 * `New` and `Dormant` are additions, and they are the point of the 2026-09-08
 * rebuild: `!active → Critical` had put 1,476 of 2,416 accounts — 61% — into
 * Critical, which is a definition rather than a finding and made the band and
 * the distribution bar useless. Dormant records are their own state now, and a
 * customer under a year old has no prior window to judge, so it is `New` rather
 * than a -100% collapse.
 */
type Band = HealthBand;

interface BandSummary {
  band: Band;
  count: number;
  pct: number;
  totalSpend: number;
  totalTravelers?: number;
  avgScore: number | null;
  /**
   * Whether this band takes a share of the distribution. False for New and
   * Dormant, whose counts are reported but whose `pct` is 0 — so a definitional
   * bucket can never own the bar again.
   */
  scored?: boolean;
}

interface Customer {
  customer_id: string;
  name: string;
  account_number: string;
  cust_ae: string | null;
  cust_csm: string | null;
  industry: string | null;
  size: string | null;
  total_spend_12m: number | null;
  travelers_12m: number | null;
  health_band: Band | null;
  health_score: number | null;
  spend_yoy_pct: number | null;
  pnrs_yoy_pct: number | null;
  latest_booking: string | null;
  status: string | null;
  /** Last 90 days vs this account's OWN trailing pace. 1.0 = on pace. */
  activity_index: number | null;
  materiality: 'low' | 'medium' | 'high' | 'strategic';
  /** Why this band, in words — so a band is never a black box. */
  health_reasons: string[];
  health_data_quality: {
    hasPriorWindow: boolean;
    hasBaseline: boolean;
    hasNps: boolean;
  };
}

interface Response {
  summary: BandSummary[];
  customers: Customer[];
  total: number;
  /** How many accounts are in a JUDGED band — the percentage denominator. */
  scoredTotal: number;
  /** How much of the book each input actually covers. */
  coverage?: {
    withPriorWindow: number;
    withBaseline: number;
    withNps: number;
  };
  band: string;
}

/**
 * ⚠️  THE OLD HINTS HERE WERE FICTION, and so was the page intro.
 *
 * This table used to claim "Score ≥ 75 · YoY positive" for Growing and
 * "Score < 35 · YoY < -25%" for Critical, while the page intro said health
 * "combines PNRs YoY (40%), Spend YoY (30%), NPS/CSAT signal (20%) and
 * Front-conversation cadence (10%)".
 *
 * None of that was implemented. The score was one of four constants
 * (20/45/68/85), `spend_yoy_pct` was hardcoded null, and there was no
 * Front-cadence input anywhere in the codebase. The screen documented a
 * methodology in detail and then did something much cruder.
 *
 * These hints now describe what api src/lib/health.ts actually computes, and
 * every band carries its own per-account reasons in the table below.
 */
const BAND_THEME: Record<
  Band,
  { hint: string; slug: string; barClass: string }
> = {
  Growing: {
    hint: 'Score ≥ 78 — growing spend and/or ahead of its own pace',
    slug: 'growing',
    barClass: 'kpi-green',
  },
  Stable: {
    hint: 'Score 55–77 — holding, on its own trailing pace',
    slug: 'stable',
    barClass: 'kpi-highlight',
  },
  'At Risk': {
    hint: 'Score 32–54 — declining, or well below its own pace',
    slug: 'at_risk',
    barClass: 'kpi-amber',
  },
  Critical: {
    hint: 'Score < 32, or a material account in decline',
    slug: 'critical',
    barClass: 'kpi-red',
  },
  // ⛔ These two used to borrow 'stable' and 'critical'. Dormant wearing the
  // critical pill is the same defect as the deleted bandBg fallback: a state
  // rendered as a severity. Both now have their own neutral pill.
  New: {
    hint: 'Under a year old — no prior window, so no trend to judge',
    slug: 'new',
    barClass: '',
  },
  Dormant: {
    hint: 'No active flag, no 12m spend, no bookings — not scored',
    slug: 'dormant',
    barClass: '',
  },
};

export default function AccountHealthPage() {
  const [data, setData] = useState<Response | null>(null);
  const [filterBand, setFilterBand] = useState<'' | Band>('');
  // Multi-select: lets a manager see combined portfolio health across their
  // team (e.g., all accounts owned by AE Sarah + AE Maria).
  const [filterAEs, setFilterAEs] = useState<string[]>([]);
  const [filterCSMs, setFilterCSMs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch<Response>('/api/account-health')
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const aes = useMemo(() => {
    if (!data) return [] as string[];
    return Array.from(
      new Set(data.customers.map((c) => c.cust_ae).filter(Boolean) as string[]),
    ).sort();
  }, [data]);
  const csms = useMemo(() => {
    if (!data) return [] as string[];
    return Array.from(
      new Set(
        data.customers.map((c) => c.cust_csm).filter(Boolean) as string[],
      ),
    ).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.customers;
    if (filterBand) rows = rows.filter((c) => c.health_band === filterBand);
    if (filterAEs.length)
      rows = rows.filter((c) => c.cust_ae && filterAEs.includes(c.cust_ae));
    if (filterCSMs.length)
      rows = rows.filter((c) => c.cust_csm && filterCSMs.includes(c.cust_csm));
    /**
     * Worst first, and UNSCORED LAST.
     *
     * `?? 0` sorted every unscored row as though it were the worst possible
     * account, so Dormant records — which are not scored at all — outranked
     * genuinely collapsing ones and filled the top of the list. Ties break on
     * spend, because between two equally unhealthy accounts the bigger one is
     * the call to make first.
     */
    return rows.sort((a, b) => {
      const as = a.health_score ?? Number.POSITIVE_INFINITY;
      const bs = b.health_score ?? Number.POSITIVE_INFINITY;
      if (as !== bs) return as - bs;
      return (b.total_spend_12m ?? 0) - (a.total_spend_12m ?? 0);
    });
  }, [data, filterBand, filterAEs, filterCSMs]);

  if (loading && !data) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <div className="loading-text">Computing portfolio health…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="page">
        <div className="banner banner-error">
          {error || 'Could not load portfolio health'}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Account Health</h1>
        <p className="page-intro">
          Portfolio view across {data.total.toLocaleString()} customers,{' '}
          <strong>{data.scoredTotal?.toLocaleString() ?? '—'}</strong> of which
          are in a judged band. A band comes from three inputs, weighted across
          whichever are available: spend against a comparable prior 12 months
          (40%), the last 90 days against the account&rsquo;s own trailing pace
          (40%), and NPS where there are at least three responses (20%). A
          missing input drops its weight rather than scoring zero. Spend
          materiality escalates a declining band, and only ever escalates. Click
          a band to filter; every row carries the reasons for its own band.
        </p>
      </div>

      <div className="kpi-row kpi-row-4">
        {data.summary.map((b) => {
          const theme = BAND_THEME[b.band];
          const active = filterBand === b.band;
          return (
            <button
              key={b.band}
              onClick={() => setFilterBand(active ? '' : b.band)}
              className={`kpi-card ${theme.barClass}`}
              style={{
                cursor: 'pointer',
                font: 'inherit',
                textAlign: 'left',
                outline: active ? '2px solid var(--accent-ring)' : 'none',
                outlineOffset: 2,
              }}
            >
              <span className="kpi-label">
                <span className={`health-pill ${theme.slug}`}>{b.band}</span>
              </span>
              <span className="kpi-value" style={{ marginTop: 4 }}>
                {b.count.toLocaleString()}
              </span>
              <span className="kpi-sub">
                {/* "of book" was wrong twice over: the percentage is a share
                    of the SCORED set, not the whole book, and for New and
                    Dormant it is 0 by design — so Dormant read "0% of book"
                    while genuinely being 9% of it. An unscored band shows its
                    count and no percentage at all. */}
                {b.scored === false
                  ? `${b.count.toLocaleString()} accounts · not scored`
                  : `${b.pct}% of scored · ${fmtMoneyShort(b.totalSpend)} spend`}
              </span>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-muted)',
                }}
              >
                {theme.hint}
                {b.avgScore != null && (
                  <span style={{ marginLeft: 8 }}>
                    · avg score {b.avgScore}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Stacked health distribution bar */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">Health distribution</span>
          <span className="section-meta">
            {(data.scoredTotal ?? data.total).toLocaleString()} scored of{' '}
            {data.total.toLocaleString()}
          </span>
        </div>
        <div className="section-body">
          {/* THE DENOMINATOR IS STATED. Percentages are of the SCORED bands
              only. Reporting Dormant as a share of the whole book is what let
              1,476 definitional rows render as 61% Critical and squash every
              actionable band into an unreadable sliver. */}
          <div className="band-denominator">
            Percentages are of the{' '}
            {(data.scoredTotal ?? data.total).toLocaleString()} accounts in a
            judged band ({SCORED_BANDS.join(', ')}). New and Dormant are states
            rather than severities, so they report counts only.
            {data.coverage ? (
              <>
                {' '}
                {data.coverage.withPriorWindow === 0
                  ? 'No account has a comparable prior 12-month window yet, so no band below reflects a spend trend — they are activity and engagement only.'
                  : `${data.coverage.withPriorWindow.toLocaleString()} accounts have a comparable prior year; ${data.coverage.withNps.toLocaleString()} have usable NPS.`}
              </>
            ) : null}
          </div>
          <div
            className="spend-mix-bar"
            style={{
              background: 'var(--border-light)',
              height: 28,
              fontSize: 'var(--fs-xs)',
              fontWeight: 700,
              color: '#fff',
            }}
            title={`${data.total.toLocaleString()} total`}
          >
            {data.summary.map((s) => {
              if (s.count === 0) return null;
              return (
                <div
                  key={s.band}
                  style={{
                    background: BAND_COLOR[s.band],
                    width: `${s.pct}%`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: s.pct > 0 ? 22 : 0,
                    transition: 'width 220ms var(--ease-out)',
                  }}
                  title={`${s.band}: ${s.count} (${s.pct}%)`}
                >
                  {s.pct >= 5 && `${s.pct}%`}
                </div>
              );
            })}
          </div>
          <div className="spend-mix-legend">
            {data.summary.map((s) => (
              <div
                key={s.band}
                className="seg-cell"
                style={{ borderLeftColor: BAND_COLOR[s.band] }}
              >
                <div className="seg-label">{s.band}</div>
                <span className="seg-amt">{s.count}</span>
                <span className="seg-pct">
                  {fmtMoneyShort(s.totalSpend)} ·{' '}
                  {(s.totalTravelers ?? 0).toLocaleString()} travelers
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Customer detail table */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">
            {filterBand ? `${filterBand} customers` : 'All customers'}
          </span>
          <span className="section-meta">
            {filtered.length.toLocaleString()} of{' '}
            {data.customers.length.toLocaleString()}
          </span>
        </div>
        <div className="section-body">
          {/* "and then from here we should be able to create lsits like email
              our sturggling customers and get that list" (Taylor, 2026-09-08).
              Routed through /api/contacts, so opted-out and bounced people are
              excluded and the pull is recorded — the same path the retired
              Contacts page used. Building it off these rows would have shipped
              them. */}
          <ListActions
            customerIds={filtered.map((c) => c.customer_id)}
            disabledReason="Filter to a band or an advisor, then build a contact list from it."
            label={filterBand ? `${filterBand} accounts` : undefined}
          />

          <div className="filter-bar">
            <span className="field-label-inline">Filter by</span>
            <select
              value={filterBand}
              data-active={filterBand ? '1' : '0'}
              onChange={(e) => setFilterBand(e.target.value as '' | Band)}
            >
              <option value="">Health band</option>
              <option>Growing</option>
              <option>Stable</option>
              <option>At Risk</option>
              <option>Critical</option>
            </select>
            <MultiSelect
              value={filterAEs}
              onChange={setFilterAEs}
              options={aes}
              placeholder="Account Executive"
              minWidth={200}
            />
            <MultiSelect
              value={filterCSMs}
              onChange={setFilterCSMs}
              options={csms}
              placeholder="CSM"
              minWidth={160}
            />
            {(filterBand || filterAEs.length > 0 || filterCSMs.length > 0) && (
              <button
                className="filter-clear"
                onClick={() => {
                  setFilterBand('');
                  setFilterAEs([]);
                  setFilterCSMs([]);
                }}
              >
                Clear
              </button>
            )}
          </div>

          {(filterAEs.length > 0 || filterCSMs.length > 0) && (
            <div
              style={{
                marginTop: 10,
                marginBottom: 6,
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-muted)',
              }}
            >
              <strong style={{ color: 'var(--text-secondary)' }}>
                Portfolio in view:
              </strong>{' '}
              {[
                filterAEs.length > 0 ? `AEs ${filterAEs.join(', ')}` : null,
                filterCSMs.length > 0 ? `CSMs ${filterCSMs.join(', ')}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>AE / CSM</th>
                  <th>Industry / Size</th>
                  <th>Health</th>
                  <th className="right">YoY Spend</th>
                  <th className="right">YoY PNRs</th>
                  <th className="right">12m Spend</th>
                  <th>Latest Booking</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr className="empty">
                    <td colSpan={8}>No customers match these filters.</td>
                  </tr>
                )}
                {filtered.map((c) => (
                  <tr key={c.customer_id}>
                    <td>
                      <Link
                        to={`/crm/customers/${encodeURIComponent(c.customer_id)}`}
                        className="cust-cell"
                        style={{ textDecoration: 'none' }}
                      >
                        <span style={avatarStyle(c.name, 32)}>
                          {initialsOf(c.name)}
                        </span>
                        <span className="cust-cell-body">
                          <span className="cust-cell-name">{c.name}</span>
                          <span className="cust-cell-meta">
                            {c.customer_id}
                          </span>
                        </span>
                      </Link>
                    </td>
                    <td>
                      <div style={{ fontSize: 'var(--fs-base)' }}>
                        {c.cust_ae || '—'}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {c.cust_csm || '—'}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 'var(--fs-base)' }}>
                        {c.industry || '—'}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {c.size || '—'}
                      </div>
                    </td>
                    <td>
                      {c.health_band ? (
                        <span
                          className={`health-pill ${BAND_THEME[c.health_band]?.slug || 'stable'}`}
                          title={
                            c.health_reasons?.length
                              ? c.health_reasons.join(' ')
                              : BAND_THEME[c.health_band]?.hint
                          }
                        >
                          {c.health_band}
                          {c.health_score != null && (
                            <span style={{ opacity: 0.7, marginLeft: 3 }}>
                              · {c.health_score}
                            </span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="right">
                      <YoyChip v={c.spend_yoy_pct} />
                    </td>
                    <td className="right">
                      <YoyChip v={c.pnrs_yoy_pct} />
                    </td>
                    <td className="right">
                      {fmtMoneyShort(c.total_spend_12m ?? 0)}
                    </td>
                    <td>
                      {c.latest_booking
                        ? fmtRelativeDate(c.latest_booking)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function YoyChip({ v }: { v: number | null }) {
  if (v == null || v === 0)
    return <span style={{ color: 'var(--text-muted)' }}>0.0%</span>;
  const positive = v > 0;
  return (
    <span
      style={{
        color: positive ? 'var(--green-strong)' : 'var(--red-strong)',
        fontWeight: 700,
      }}
    >
      {positive ? '▲' : '▼'} {Math.abs(v).toFixed(1)}%
    </span>
  );
}

function fmtMoneyShort(v: number | null): string {
  if (v == null || !isFinite(v) || v === 0) return '$0';
  if (v >= 1_000_000_000) return '$' + (v / 1_000_000_000).toFixed(2) + 'B';
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  if (v >= 10_000) return '$' + (v / 1_000).toFixed(0) + 'k';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtRelativeDate(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return d.toLocaleDateString();
}
